import { useEffect, useRef, useState, useMemo, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  SERVICE_MODEL_OPTIONS,
  questionnaireUsesServiceModels,
  serviceModelsRequired,
  validateServiceModels,
} from "@/lib/serviceModels";
import { getQuestionnaireDisplayTitle } from "@/lib/questionnaireDisplayTitle";
import { BrandedLogo } from "@/components/ui/BrandedLogo";
import { getLogoUrl } from "@/utils/getLogoUrl";
import { LogoUpload, uploadLogoForEntity } from "@/components/admin/LogoUpload";
import type { LogoPathArgs } from "@/lib/slugify";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Plus, Building2, Loader2, Clock, BarChart3, Shield, Search, X, ArrowUpDown, ArrowUp, ArrowDown, AlertTriangle, ChevronRight, ChevronDown } from "lucide-react";
import { isSelfAssessmentOnly, isHrvQuestionnaire } from "@/lib/questionnaireType";
import PmAssessmentRollupsPanel from "@/components/portfolio/PmAssessmentRollupsPanel";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { formatDistanceToNow, format, differenceInHours } from "date-fns";

/**
 * Tier 2 Portfolio Manager Dashboard.
 *
 * Full PM experience:
 *  - Portfolio average / status / distribution summary cards
 *  - HRV portfolio exposure widget
 *  - Companies table with filters/sorting
 *  - telecom_v2 expandable rollups (CSV / NIST / Findings / CAP) via
 *    PmAssessmentRollupsPanel + pm_get_assessment_rollups RPC
 */

interface SubOrg {
  sub_org_id: string;
  role: string;
  sub_organizations: { id: string; name: string } | null;
}

interface CategoryQuestionnaire {
  questionnaire_key: string;
  is_default: boolean;
  q: { id: string; meta: unknown } | null;
}

interface CompanyRow {
  portfolio_id: string;
  assessment_id: string | null;
  name: string;
  logo_url: string | null;
  questionnaire_id: string | null;
  rating: string;
  score: number | null;
  respondent_score: number | null;
  reviewer_score: number | null;
  completion_pct: number;
  assessment_status: string;
  current_cycle: number;
  last_updated: string | null;
  hrv_composite_score: number | null;
  hrv_exposure_label: string | null;
  hrv_escalation_required: boolean;
  hrv_domain_scores: Array<{ domain: string; score: number; exposure_label: string }> | null;
}

interface HrvCompanyDetail {
  portfolio_id: string;
  name: string;
  score_100: number | null;
  vendors: Array<{ vendor_name: string; vendor_risk_level: string; criticality: string }>;
}

interface HrvRiskMap {
  high_risk: number;
  moderate_risk: number;
  low_risk: number;
  not_assessed: number;
  total: number;
  high_risk_companies: HrvCompanyDetail[];
  moderate_risk_companies: HrvCompanyDetail[];
  low_risk_companies: HrvCompanyDetail[];
}

interface PortfolioDashboardData {
  portfolio_average_score: number;
  portfolio_rating: string;
  avg_respondent_score: number;
  avg_reviewer_score: number;
  distribution: { Minimal: number; Adequate: number; Strong: number; Extensive: number };
  assessment_status_counts: { "Not Started": number; "In Progress": number; Closed: number };
  companies: CompanyRow[];
}

const CATEGORIES = [
  { value: "Cybersecurity Starter Assessments", label: "Cybersecurity Starter Assessments" },
  { value: "Telecom (Fixed/ISP)", label: "Telecommunications" },
  { value: "Media", label: "Media" },
  { value: "Private Equity", label: "Private Equity" },
  { value: "Data Center", label: "Data Center" },
  { value: "Gen AI", label: "Gen AI" },
  { value: "Risk Exposure", label: "Risk Exposure" },
  { value: "Pre-Investment Triage", label: "Pre-Investment Triage" },
];

// Phase 1 (Service Model Foundation) — shared source of truth.
const TELECOM_SERVICE_MODEL_OPTIONS = SERVICE_MODEL_OPTIONS;

const RATING_STYLES: Record<string, { color: string; bg: string }> = {
  Extensive: { color: "text-green-600", bg: "bg-green-100 text-green-700 border-green-200" },
  Strong: { color: "text-blue-600", bg: "bg-blue-100 text-blue-700 border-blue-200" },
  Adequate: { color: "text-yellow-600", bg: "bg-yellow-100 text-yellow-700 border-yellow-200" },
  Minimal: { color: "text-orange-600", bg: "bg-orange-100 text-orange-700 border-orange-200" },
  "Not Rated": { color: "text-muted-foreground", bg: "bg-muted text-muted-foreground border-border" },
};

const DISTRIBUTION_BARS: { key: keyof PortfolioDashboardData["distribution"]; label: string; color: string }[] = [
  { key: "Extensive", label: "Extensive", color: "bg-green-500" },
  { key: "Strong", label: "Strong", color: "bg-blue-500" },
  { key: "Adequate", label: "Adequate", color: "bg-yellow-500" },
  { key: "Minimal", label: "Minimal", color: "bg-orange-500" },
];

type SortColumn = "score" | "rating" | "completion" | "status" | "cycle" | "last_updated";
type SortDir = "asc" | "desc";

const STATUS_ORDER: Record<string, number> = { "Not Started": 0, "In Progress": 1, Closed: 2 };

function formatLastUpdated(ts: string | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  const hours = differenceInHours(new Date(), d);
  if (hours < 24) return formatDistanceToNow(d, { addSuffix: true });
  return format(d, "MMM d, yyyy");
}

function getEffectiveScore(c: CompanyRow): number | null {
  return isSelfAssessmentOnly(c.questionnaire_id) ? c.respondent_score : c.score;
}

export default function Tier2Dashboard() {
  useEffect(() => {
    console.log("Rendering Tier2Dashboard (build: tier-split-2026-04-30)");
  }, []);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subOrgs, setSubOrgs] = useState<SubOrg[]>([]);
  const [selectedSubOrgId, setSelectedSubOrgId] = useState<string | null>(null);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createCategory, setCreateCategory] = useState("");
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState("");
  const [availableAssessments, setAvailableAssessments] = useState<CategoryQuestionnaire[]>([]);
  const [serviceModels, setServiceModels] = useState<string[]>([]);
  const [serviceModelOther, setServiceModelOther] = useState("");
  const createRequestKeyRef = useRef<string | null>(null);
  const [loadingAssessments, setLoadingAssessments] = useState(false);
  const [creating, setCreating] = useState(false);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);

  // Portfolio dashboard RPC data
  const [portfolioDashboardData, setPortfolioDashboardData] = useState<PortfolioDashboardData | null>(null);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [hrvRiskMap, setHrvRiskMap] = useState<HrvRiskMap | null>(null);
  const [triageBands, setTriageBands] = useState<Record<string, "Low" | "Medium" | "High" | null>>({});
  const [hrvOpenSections, setHrvOpenSections] = useState<Record<string, boolean>>({});

  const [orgContext, setOrgContext] = useState<{
    clientName: string | null;
    subOrgName: string | null;
    clientLogo: string | null;
    subOrgLogo: string | null;
  }>({ clientName: null, subOrgName: null, clientLogo: null, subOrgLogo: null });

  const [searchQuery, setSearchQuery] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn>("score");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [filterType, setFilterType] = useState<string>("all");

  const [expandedRollups, setExpandedRollups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    async function loadSubOrgs() {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate("/login", { replace: true });
        return;
      }
      const { data: contextData, error: contextErr } = await (supabase as any).rpc("pm_my_sub_org_contexts");
      if (contextErr) {
        console.error("Error loading PM context via RPC:", contextErr);
      } else if (contextData && contextData.length > 0) {
        const first = contextData[0];
        setOrgContext({
          clientName: first.client_name || null,
          subOrgName: first.sub_org_name || null,
          clientLogo: first.client_logo || null,
          subOrgLogo: first.sub_org_logo || null,
        });
        setSelectedSubOrgId(first.sub_org_id);
        const mapped = contextData.map((c: any) => ({
          sub_org_id: c.sub_org_id,
          role: "portfolio_manager",
          sub_organizations: { id: c.sub_org_id, name: c.sub_org_name },
        }));
        setSubOrgs(mapped);
      }
      setLoading(false);
    }
    loadSubOrgs();
  }, [navigate]);

  useEffect(() => {
    async function loadDashboard() {
      if (!selectedSubOrgId) return;
      setLoadingDashboard(true);
      try {
        const [dashRes, hrvRes] = await Promise.all([
          (supabase.rpc as any)("pm_get_portfolio_dashboard", { p_sub_org_id: selectedSubOrgId }),
          (supabase.rpc as any)("pm_get_hrv_risk_map", { p_sub_org_id: selectedSubOrgId }),
        ]);
        if (dashRes.error) console.error("[PM] pm_get_portfolio_dashboard error:", dashRes.error);
        else setPortfolioDashboardData(dashRes.data as PortfolioDashboardData);
        if (hrvRes.error) console.error("[PM] pm_get_hrv_risk_map error:", hrvRes.error);
        else setHrvRiskMap(hrvRes.data as HrvRiskMap);
      } catch (err) {
        console.error("[PM] dashboard load exception:", err);
      }
      setLoadingDashboard(false);
    }
    loadDashboard();
  }, [selectedSubOrgId]);

  // Fetch triage bands for telecom_pre_investment_triage_v1 assessments
  useEffect(() => {
    const companies = portfolioDashboardData?.companies ?? [];
    const triageCompanies = companies.filter(
      (c) => c.questionnaire_id === "telecom_pre_investment_triage_v1" && c.assessment_id
    );
    if (triageCompanies.length === 0) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        triageCompanies.map(async (c) => {
          const { data, error } = await (supabase.rpc as any)("respondent_get_triage_summary", {
            p_assessment_id: c.assessment_id,
          });
          if (error || !data) return [c.portfolio_id, null] as const;
          return [c.portfolio_id, (data?.overall_band ?? null) as "Low" | "Medium" | "High" | null] as const;
        })
      );
      if (cancelled) return;
      setTriageBands((prev) => {
        const next = { ...prev };
        for (const [pid, band] of entries) next[pid] = band;
        return next;
      });
    })();
    return () => { cancelled = true; };
  }, [portfolioDashboardData]);

  const processedCompanies = useMemo(() => {
    if (!portfolioDashboardData?.companies) return [];
    let rows = [...portfolioDashboardData.companies];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rows = rows.filter((c) => c.name.toLowerCase().includes(q));
    }
    if (filterStatus !== "all") rows = rows.filter((c) => c.assessment_status === filterStatus);
    if (filterType === "self") rows = rows.filter((c) => isSelfAssessmentOnly(c.questionnaire_id));
    else if (filterType === "full") rows = rows.filter((c) => !isSelfAssessmentOnly(c.questionnaire_id));
    rows.sort((a, b) => {
      let cmp = 0;
      switch (sortColumn) {
        case "score":
        case "rating":
          cmp = (getEffectiveScore(a) ?? -1) - (getEffectiveScore(b) ?? -1);
          break;
        case "completion":
          cmp = a.completion_pct - b.completion_pct;
          break;
        case "status":
          cmp = (STATUS_ORDER[a.assessment_status] ?? 0) - (STATUS_ORDER[b.assessment_status] ?? 0);
          break;
        case "cycle":
          cmp = a.current_cycle - b.current_cycle;
          break;
        case "last_updated":
          cmp = new Date(a.last_updated ?? 0).getTime() - new Date(b.last_updated ?? 0).getTime();
          break;
      }
      return sortDir === "desc" ? -cmp : cmp;
    });
    return rows;
  }, [portfolioDashboardData, searchQuery, filterStatus, filterType, sortColumn, sortDir]);

  const distributionMax = useMemo(() => {
    if (!portfolioDashboardData?.distribution) return 1;
    return Math.max(1, ...Object.values(portfolioDashboardData.distribution));
  }, [portfolioDashboardData]);

  const hasAnyFullAssessment = useMemo(() => {
    if (!portfolioDashboardData?.companies) return false;
    return portfolioDashboardData.companies.some((c) => !isSelfAssessmentOnly(c.questionnaire_id));
  }, [portfolioDashboardData]);

  const hasAnySelfAssessment = useMemo(() => {
    if (!portfolioDashboardData?.companies) return false;
    return portfolioDashboardData.companies.some((c) => isSelfAssessmentOnly(c.questionnaire_id));
  }, [portfolioDashboardData]);

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortColumn(col); setSortDir("desc"); }
  };

  const SortIcon = ({ col }: { col: SortColumn }) => {
    if (sortColumn !== col) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDir === "asc" ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const filtersActive = searchQuery || filterStatus !== "all" || filterType !== "all";
  const clearFilters = () => { setSearchQuery(""); setFilterStatus("all"); setFilterType("all"); };

  const handleCategoryChange = async (category: string) => {
    setCreateCategory(category);
    setLoadingAssessments(true);
    setAvailableAssessments([]);
    setSelectedQuestionnaire("");
    setServiceModels([]);
    const { data: qRows, error: qErr } = await (supabase.rpc as any)("pm_list_questionnaires_by_category", {
      p_category: category,
    });
    if (qErr || !qRows || qRows.length === 0) { setLoadingAssessments(false); return; }
    const merged = (qRows as any[]).map((q: any) => ({
      questionnaire_key: q.id,
      is_default: false,
      q: { id: q.id, meta: q.meta },
    }));
    setAvailableAssessments(merged);
    const defaultRow = merged.find((r) => r.is_default) ?? merged[0];
    if (defaultRow?.questionnaire_key) setSelectedQuestionnaire(defaultRow.questionnaire_key);
    setLoadingAssessments(false);
  };

  // Phase 1 (revised) — explicit capability gate, mirrors questionnaire_capabilities.
  const isTelecomV2 = questionnaireUsesServiceModels(selectedQuestionnaire);

  const handleCreatePortfolio = async () => {
    if (!createName.trim() || !createCategory || !selectedQuestionnaire || !selectedSubOrgId) {
      toast.error("Please fill in all fields");
      return;
    }
    if (isTelecomV2) {
      const validation = validateServiceModels(serviceModels, {
        required: serviceModelsRequired(selectedQuestionnaire),
        otherDescription: serviceModelOther,
      });
      if (!validation.ok) {
        toast.error(validation.error!);
        return;
      }
    }
    setCreating(true);
    // Phase 1 (revised) — transactional create: portfolio + Service Models in one
    // RPC. A retry with the same request key never creates a duplicate portfolio.
    if (!createRequestKeyRef.current) createRequestKeyRef.current = crypto.randomUUID();
    const { data: rpcData, error } = await (supabase.rpc as any)(
      "pm_create_portfolio_with_service_models",
      {
        p_sub_org_id: selectedSubOrgId,
        p_name: createName.trim(),
        p_category: createCategory,
        p_questionnaire_id: selectedQuestionnaire,
        p_models: isTelecomV2 ? serviceModels : null,
        p_other_description: serviceModelOther || null,
        p_request_key: createRequestKeyRef.current,
      },
    );
    if (error) {
      console.error("Error creating portfolio:", error);
      toast.error(error.message || "Failed to create portfolio company");
    } else {
      createRequestKeyRef.current = null;
      if (pendingLogoFile && rpcData) {
        try {
          const pathArgs: LogoPathArgs = {
            level: "portfolio",
            clientName: orgContext.clientName || "client",
            subOrgName: orgContext.subOrgName || "sub-org",
            companyName: createName.trim(),
            entityId: rpcData,
          };
          await uploadLogoForEntity(pendingLogoFile, pathArgs);
        } catch (logoErr) {
          console.error("Logo upload failed:", logoErr);
          toast.error("Client created but logo upload failed");
        }
      }
      toast.success("Portfolio company created successfully");
      setShowCreateModal(false);
      setCreateName(""); setCreateCategory(""); setSelectedQuestionnaire("");
      setAvailableAssessments([]); setPendingLogoFile(null); setServiceModels([]); setServiceModelOther("");
      const [dashRes, hrvRes] = await Promise.all([
        (supabase.rpc as any)("pm_get_portfolio_dashboard", { p_sub_org_id: selectedSubOrgId }),
        (supabase.rpc as any)("pm_get_hrv_risk_map", { p_sub_org_id: selectedSubOrgId }),
      ]);
      if (dashRes.data) setPortfolioDashboardData(dashRes.data as PortfolioDashboardData);
      if (hrvRes.data) setHrvRiskMap(hrvRes.data as HrvRiskMap);
    }
    setCreating(false);
  };

  const getRatingBadge = (rating: string) => {
    const style = RATING_STYLES[rating] ?? RATING_STYLES["Not Rated"];
    return <Badge className={`border ${style.bg}`}>{rating}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "Closed": return <Badge className="bg-green-500 text-white hover:bg-green-600">Closed</Badge>;
      case "In Progress": return <Badge className="bg-blue-500 text-white hover:bg-blue-600">In Progress</Badge>;
      case "Not Started": return <Badge variant="secondary">Not Started</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <AppLayout title="Portfolio Manager">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </AppLayout>
    );
  }

  if (subOrgs.length === 0) {
    return (
      <AppLayout title="Portfolio Manager">
        <div className="container mx-auto py-8">
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Building2 className="h-16 w-16 text-muted-foreground mb-4" />
              <h2 className="text-xl font-semibold mb-2">No Sub-Organizations Assigned</h2>
              <p className="text-muted-foreground text-center">
                You are not assigned as a portfolio manager to any sub-organizations.
                <br />
                Please contact your administrator.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const d = portfolioDashboardData;
  const ratingStyle = RATING_STYLES[d?.portfolio_rating ?? "Not Rated"] ?? RATING_STYLES["Not Rated"];
  const scoreBasisLabel = hasAnyFullAssessment && hasAnySelfAssessment
    ? "Based on Reviewer & Self-Assessment Scores"
    : hasAnySelfAssessment
    ? "Based on Respondent Self-Assessments"
    : "Based on Reviewer Scores";

  return (
    <AppLayout title="Portfolio Manager">
      <TooltipProvider>
        <div className="container mx-auto py-8 space-y-6">
          {(orgContext.clientName || orgContext.subOrgName) && (
            <div className="flex items-center gap-3 text-sm">
              <BrandedLogo
                src={getLogoUrl(orgContext.subOrgLogo) || getLogoUrl(orgContext.clientLogo)}
                alt={orgContext.subOrgName || orgContext.clientName || "Client"}
                className="h-10 w-auto"
                fallback={<Building2 className="h-5 w-5 text-muted-foreground" />}
                loading={loading}
              />
              <span className="font-semibold text-foreground">
                {orgContext.clientName && orgContext.subOrgName
                  ? `${orgContext.clientName} - ${orgContext.subOrgName}`
                  : orgContext.clientName || orgContext.subOrgName}
              </span>
            </div>
          )}

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Portfolio Manager Dashboard</h1>
              <p className="text-muted-foreground">
                Manage portfolio companies for your assigned sub-organizations
              </p>
            </div>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Portfolio Company
            </Button>
          </div>

          {loadingDashboard ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : d && (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Shield className="h-4 w-4" />
                      Portfolio Average Rating
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-3xl font-bold ${ratingStyle.color}`}>{d.portfolio_rating}</p>
                    <p className="text-lg text-muted-foreground mt-1">
                      {d.portfolio_average_score}<span className="text-sm"> / 100</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">{scoreBasisLabel}</p>
                    <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
                      <span>Avg Respondent Score: <strong className="text-foreground">{d.avg_respondent_score}</strong></span>
                      <span>Avg Reviewer Score: <strong className="text-foreground">{d.avg_reviewer_score}</strong></span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Assessment Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 pt-1">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Not Started</span>
                        <span className="text-lg font-semibold">{d.assessment_status_counts["Not Started"]}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">In Progress</span>
                        <span className="text-lg font-semibold text-blue-600">{d.assessment_status_counts["In Progress"]}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">Closed</span>
                        <span className="text-lg font-semibold text-green-600">{d.assessment_status_counts["Closed"]}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" />
                      Resilience Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2.5 pt-1">
                      {DISTRIBUTION_BARS.map(({ key, label, color }) => {
                        const count = d.distribution[key] ?? 0;
                        const pct = distributionMax > 0 ? (count / distributionMax) * 100 : 0;
                        return (
                          <div key={key} className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground w-16 shrink-0">{label}</span>
                            <div className="flex-1 h-4 bg-muted rounded-full overflow-hidden">
                              <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-sm font-semibold w-6 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {hrvRiskMap && (hrvRiskMap.high_risk + hrvRiskMap.moderate_risk + hrvRiskMap.low_risk) > 0 && (
                <Card className="mt-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4" />
                      Portfolio HRV Exposure
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {(() => {
                      const high = (hrvRiskMap.high_risk_companies ?? []).length;
                      const mod = (hrvRiskMap.moderate_risk_companies ?? []).length;
                      const low = (hrvRiskMap.low_risk_companies ?? []).length;
                      const total = Math.max(1, high + mod + low);
                      return (
                        <div className="mb-4">
                          <p className="text-xs font-medium text-muted-foreground mb-2">Risk Distribution</p>
                          <div className="space-y-2">
                            {[
                              { label: "High Risk", count: high, dot: "🔴", color: "bg-red-500" },
                              { label: "Moderate Risk", count: mod, dot: "🟡", color: "bg-yellow-500" },
                              { label: "Low Risk", count: low, dot: "🟢", color: "bg-green-500" },
                            ].map((r) => (
                              <div key={r.label} className="flex items-center gap-3">
                                <span className="text-xs w-28 shrink-0 flex items-center gap-1.5">
                                  <span>{r.dot}</span>
                                  <span className="text-muted-foreground">{r.label}</span>
                                </span>
                                <div className="w-40 h-2 bg-muted rounded-md overflow-hidden shrink-0">
                                  <div
                                    className={`h-full ${r.color} rounded-md transition-all`}
                                    style={{ width: `${(r.count / total) * 100}%` }}
                                  />
                                </div>
                                <span className="text-xs font-semibold w-4 text-right">{r.count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })()}
                    <div className="space-y-1">
                      {([
                        { key: "high_risk" as const, label: "High Risk", dot: "🔴", barColor: "bg-red-500", companies: hrvRiskMap.high_risk_companies ?? [] },
                        { key: "moderate_risk" as const, label: "Moderate Risk", dot: "🟡", barColor: "bg-yellow-500", companies: hrvRiskMap.moderate_risk_companies ?? [] },
                        { key: "low_risk" as const, label: "Low Risk", dot: "🟢", barColor: "bg-green-500", companies: hrvRiskMap.low_risk_companies ?? [] },
                      ]).filter(row => row.companies.length > 0).map((row) => (
                        <Collapsible
                          key={row.key}
                          open={hrvOpenSections[row.key] ?? false}
                          onOpenChange={(open) => setHrvOpenSections(prev => ({ ...prev, [row.key]: open }))}
                        >
                          <CollapsibleTrigger className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-sm transition-colors hover:bg-muted/60">
                            <span className="flex items-center gap-2">
                              <ChevronRight className={`h-4 w-4 transition-transform ${hrvOpenSections[row.key] ? "rotate-90" : ""}`} />
                              <span>{row.dot}</span>
                              <span className="font-medium">{row.label}</span>
                              <span className="text-xs text-muted-foreground">({row.companies.length})</span>
                            </span>
                          </CollapsibleTrigger>
                          <CollapsibleContent>
                            <div className="ml-8 space-y-2 pb-2">
                              {row.companies.map((company) => (
                                <div key={company.portfolio_id} className="rounded-lg border bg-muted/30 px-3 py-2">
                                  <div className="flex items-center justify-between">
                                    <button
                                      className="text-sm font-medium text-primary hover:underline text-left"
                                      onClick={() => navigate(`/org-admin/${company.portfolio_id}/assessments`)}
                                    >
                                      {company.name}
                                    </button>
                                    {company.score_100 != null && (
                                      <span className="text-sm font-semibold">
                                        {company.score_100}<span className="text-muted-foreground font-normal"> / 100</span>
                                      </span>
                                    )}
                                  </div>
                                  {company.score_100 != null && (
                                    <div className="mt-1.5 h-2 w-full bg-muted rounded-full overflow-hidden">
                                      <div
                                        className={`h-full rounded-full transition-all ${row.barColor}`}
                                        style={{ width: `${Math.min(company.score_100, 100)}%` }}
                                      />
                                    </div>
                                  )}
                                  {(() => {
                                    const isHigh = row.key === "high_risk";
                                    const isMod = row.key === "moderate_risk";
                                    if (isHigh) return (
                                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-red-600">
                                        <AlertTriangle className="h-3 w-3" />
                                        <span className="font-medium">Full Technical Assessment Recommended</span>
                                      </div>
                                    );
                                    if (isMod) return (
                                      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-yellow-600">
                                        <AlertTriangle className="h-3 w-3" />
                                        <span className="font-medium">Targeted Review Recommended</span>
                                      </div>
                                    );
                                    return null;
                                  })()}
                                  {company.vendors && company.vendors.length > 0 && (
                                    <div className="mt-2 text-xs text-muted-foreground">
                                      <span className="font-medium text-foreground">Vendors:</span>
                                      <div className="mt-0.5 space-y-0.5">
                                        {company.vendors.map((v, i) => {
                                          const vDot = v.vendor_risk_level === "High" ? "🔴" : v.vendor_risk_level === "Moderate" ? "🟡" : "🟢";
                                          return (
                                            <div key={i} className="flex items-center gap-1.5">
                                              <span>{vDot}</span>
                                              <span>{v.vendor_name}</span>
                                              <span className="text-muted-foreground">({v.vendor_risk_level} Risk Vendor)</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">Portfolio Companies</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative w-64">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search company name..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[150px] h-9"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-[180px] h-9"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="full">Full Assessment</SelectItem>
                    <SelectItem value="self">Self-Assessment Only</SelectItem>
                  </SelectContent>
                </Select>
                {filtersActive && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs">
                    <X className="h-3 w-3 mr-1" />Clear Filters
                  </Button>
                )}
              </div>

              {loadingDashboard ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              ) : processedCompanies.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {filtersActive ? "No companies match your filters." : "No portfolio companies yet. Create one to get started."}
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-8"></TableHead>
                      <TableHead>Company Name</TableHead>
                      <TableHead className="cursor-pointer select-none" onClick={() => handleSort("rating")}>
                        <span className="flex items-center">Rating <SortIcon col="rating" /></span>
                      </TableHead>
                      <TableHead className="cursor-pointer select-none" onClick={() => handleSort("score")}>
                        <span className="flex items-center">Score <SortIcon col="score" /></span>
                      </TableHead>
                      <TableHead className="cursor-pointer select-none" onClick={() => handleSort("completion")}>
                        <span className="flex items-center">Completion <SortIcon col="completion" /></span>
                      </TableHead>
                      <TableHead className="cursor-pointer select-none" onClick={() => handleSort("status")}>
                        <span className="flex items-center">Status <SortIcon col="status" /></span>
                      </TableHead>
                      <TableHead className="cursor-pointer select-none" onClick={() => handleSort("cycle")}>
                        <span className="flex items-center">Cycle <SortIcon col="cycle" /></span>
                      </TableHead>
                      <TableHead className="cursor-pointer select-none" onClick={() => handleSort("last_updated")}>
                        <span className="flex items-center">Last Updated <SortIcon col="last_updated" /></span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {processedCompanies.map((company) => {
                      const selfOnly = isSelfAssessmentOnly(company.questionnaire_id);
                      const isHrv = isHrvQuestionnaire(company.questionnaire_id);
                      const displayScore = isHrv ? null : (selfOnly ? company.respondent_score : company.score);
                      const hrvScore100 = company.hrv_composite_score != null
                        ? Math.round(((company.hrv_composite_score - 1) / 2) * 100)
                        : null;
                      let scoreTooltip: string;
                      if (isHrv) {
                        scoreTooltip = company.hrv_exposure_label
                          ? `HRV Exposure: ${company.hrv_exposure_label}\nScore: ${hrvScore100 ?? "—"} / 100`
                          : "HRV Self-Assessment";
                      } else if (selfOnly) {
                        scoreTooltip = "Self-Assessment Score (Respondent Only)";
                      } else if (company.reviewer_score != null) {
                        scoreTooltip = `Reviewer Score: ${Math.round(company.reviewer_score)}\nRespondent Score: ${company.respondent_score != null ? Math.round(company.respondent_score) : "—"}`;
                      } else {
                        scoreTooltip = `Respondent Score: ${company.respondent_score != null ? Math.round(company.respondent_score) : "—"}\n(No reviewer score yet)`;
                      }
                      const hrvExposureBadgeClass = company.hrv_exposure_label === "High Exposure"
                        ? "bg-red-100 text-red-700 border-red-300"
                        : company.hrv_exposure_label === "Moderate Exposure"
                        ? "bg-yellow-100 text-yellow-700 border-yellow-300"
                        : "bg-green-100 text-green-700 border-green-300";
                      const supportsRollups =
                        company.questionnaire_id === "telecom_v2" && !!company.assessment_id;
                      const isExpanded = !!expandedRollups[company.portfolio_id];

                      return (
                        <Fragment key={company.portfolio_id}>
                          <TableRow
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => navigate(`/org-admin/${company.portfolio_id}/assessments`)}
                          >
                            <TableCell
                              className="w-8"
                              onClick={(e) => {
                                if (!supportsRollups) return;
                                e.stopPropagation();
                                setExpandedRollups((prev) => ({
                                  ...prev,
                                  [company.portfolio_id]: !prev[company.portfolio_id],
                                }));
                              }}
                            >
                              {supportsRollups ? (
                                <button
                                  type="button"
                                  aria-label={isExpanded ? "Collapse rollups" : "Expand rollups"}
                                  className="p-1 rounded hover:bg-muted"
                                >
                                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </button>
                              ) : null}
                            </TableCell>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2 whitespace-nowrap">
                                {company.name}
                                {isHrv ? (
                                  <Badge variant="outline" className="text-xs border-orange-300 text-orange-600 dark:border-orange-700 dark:text-orange-400 whitespace-nowrap">
                                    HRV Assessment
                                  </Badge>
                                ) : company.questionnaire_id === "telecom_pre_investment_triage_v1" ? (
                                  <Badge variant="outline" className="text-xs border-purple-300 text-purple-600 dark:border-purple-700 dark:text-purple-400 whitespace-nowrap">
                                    Triage
                                  </Badge>
                                ) : selfOnly ? (
                                  <Badge variant="outline" className="text-xs border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400 whitespace-nowrap">
                                    Self-Assessment Only
                                  </Badge>
                                ) : null}
                              </div>
                            </TableCell>
                            <TableCell>
                              {company.questionnaire_id === "telecom_pre_investment_triage_v1" ? (
                                (() => {
                                  const band = triageBands[company.portfolio_id];
                                  if (!band) {
                                    return <Badge className="border bg-muted text-muted-foreground border-border">Not Yet Rated</Badge>;
                                  }
                                  const cls = band === "High"
                                    ? "bg-red-100 text-red-700 border-red-300"
                                    : band === "Medium"
                                    ? "bg-yellow-100 text-yellow-700 border-yellow-300"
                                    : "bg-green-100 text-green-700 border-green-300";
                                  return <Badge className={`border ${cls}`}>{`${band} Investment Risk`}</Badge>;
                                })()
                              ) : isHrv ? (
                                company.hrv_exposure_label ? (
                                  <Badge className={`border ${hrvExposureBadgeClass}`}>{company.hrv_exposure_label}</Badge>
                                ) : (
                                  <Badge className="border bg-muted text-muted-foreground border-border">Not Yet Rated</Badge>
                                )
                              ) : company.completion_pct === 0 ? (
                                <Badge className="border bg-muted text-muted-foreground border-border">Not Yet Rated</Badge>
                              ) : (
                                getRatingBadge(company.rating)
                              )}
                            </TableCell>
                            <TableCell>
                              {company.questionnaire_id === "telecom_pre_investment_triage_v1" ? (
                                <span className="text-muted-foreground">—</span>
                              ) : isHrv ? (
                                hrvScore100 != null ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="font-semibold cursor-default">
                                        {hrvScore100}<span className="text-muted-foreground font-normal"> / 100</span>
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent className="whitespace-pre-line text-xs max-w-xs">{scoreTooltip}</TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <span className="text-muted-foreground">—</span>
                                )
                              ) : company.completion_pct === 0 ? (
                                <span className="text-muted-foreground">—</span>
                              ) : displayScore != null ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="font-semibold cursor-default">
                                      {displayScore}<span className="text-muted-foreground font-normal">/100</span>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent className="whitespace-pre-line text-xs max-w-xs">{scoreTooltip}</TooltipContent>
                                </Tooltip>
                              ) : (
                                <span className="text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Progress value={company.completion_pct} className="h-2 w-16" />
                                <span className="text-sm text-muted-foreground">{company.completion_pct}%</span>
                              </div>
                            </TableCell>
                            <TableCell>{getStatusBadge(company.assessment_status)}</TableCell>
                            <TableCell className="text-center text-sm text-muted-foreground">{company.current_cycle}</TableCell>
                            <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{formatLastUpdated(company.last_updated)}</TableCell>
                          </TableRow>
                          {supportsRollups && isExpanded && (
                            <TableRow className="bg-muted/30 hover:bg-muted/30">
                              <TableCell colSpan={8} className="p-4">
                                <PmAssessmentRollupsPanel assessmentId={company.assessment_id as string} />
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>

        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Portfolio Company</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="name">Portfolio Company Name</Label>
                <Input
                  id="name"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="Enter company name"
                />
              </div>
              <LogoUpload onUploaded={() => {}} onFileSelected={(file) => setPendingLogoFile(file)} />
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={createCategory} onValueChange={handleCategoryChange}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Assessment</Label>
                <Select
                  value={selectedQuestionnaire}
                  onValueChange={(v) => {
                    setSelectedQuestionnaire(v);
                    if (v !== "telecom_v2") setServiceModels([]);
                  }}
                  disabled={loadingAssessments}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={loadingAssessments ? "Loading assessments..." : !createCategory ? "Select category first" : "Select assessment"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableAssessments.map((row) => (
                      <SelectItem key={row.questionnaire_key} value={row.questionnaire_key}>
                        {getQuestionnaireDisplayTitle(row.questionnaire_key, row.q?.meta as Record<string, unknown>)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isTelecomV2 && (
                <div className="space-y-2">
                  <Label>Telecom Service Model(s) *</Label>
                  <p className="text-xs text-muted-foreground">
                    Select all that apply. This is stored on the Portfolio Company
                    and inherited by reviewers and respondents.
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {TELECOM_SERVICE_MODEL_OPTIONS.map((m) => {
                      const active = serviceModels.includes(m);
                      return (
                        <button
                          key={m}
                          type="button"
                          onClick={() =>
                            setServiceModels((prev) =>
                              prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m],
                            )
                          }
                          className={`text-xs rounded-full border px-2.5 py-1 transition-colors ${
                            active
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-background text-muted-foreground border-border hover:bg-muted"
                          }`}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                  {serviceModels.includes("Other") && (
                    <Input
                      placeholder="Describe the operating model"
                      value={serviceModelOther}
                      onChange={(e) => setServiceModelOther(e.target.value)}
                    />
                  )}
                  {serviceModels.length === 0 && serviceModelsRequired(selectedQuestionnaire) && (
                    <p className="text-xs text-destructive">Select at least one service model.</p>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button
                onClick={handleCreatePortfolio}
                disabled={creating || !createName.trim() || !createCategory || !selectedQuestionnaire || (serviceModelsRequired(selectedQuestionnaire) && serviceModels.length === 0)}
              >
                {creating ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</>
                ) : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </TooltipProvider>
    </AppLayout>
  );
}
